import { Injectable } from "@angular/core";
import { Resolve } from "@angular/router";
import { of, throwError } from "rxjs";
import { EhrPatientService } from "./ehr-patient.service";
import { IIdentifiers } from "../model/identifiers";
import { HttpHeaders, HttpClient, HttpErrorResponse } from "@angular/common/http";
import {map, mergeMap, catchError, tap, switchMap, filter, take, finalize} from "rxjs/operators";
import { SpinnerService } from "../spinner/spinner.service";
import { UtilityService } from "../shared/service/utility.service";
import { IPatientContext } from "../model/patient.";
import { AngularAppLogger } from "../angular-app-logger";
import {AppStateService} from "../shared/service/app.state.service";

@Injectable()
export class PatientResolver implements Resolve<IPatientContext[]> {
  constructor(
    readonly patientDetailService: EhrPatientService,
    readonly http: HttpClient,
    readonly spinner: SpinnerService,
    readonly utilityService: UtilityService,
    readonly appLogger: AngularAppLogger,
    readonly appState: AppStateService,
  ) { }

  resolve(): any {
    console.log("Patient Resolver");
    const issurl = localStorage.getItem("iss");

    return this.appState.patient$.pipe(
      take(1),
      switchMap((patientID) => {
        console.log("in switchmap");
        if (!issurl || !patientID) {
          const errorMessage = `Patient context missing during SMART launch. ISS: ${issurl || 'NotProvided'}, Patient ID: ${patientID || 'NotProvided'}. SMART launch requires patient-scoped context.`;
          this.appLogger.logError(errorMessage, {
            url: issurl,
            allowDuplicate: false
          });
          return of(null);
        }

        console.log("Resolver patientID:", patientID);

        const cachedData = localStorage.getItem(`patientData_${patientID}`);
        const parsedCachedData = cachedData ? JSON.parse(cachedData) : null;
        if (cachedData) {
          console.log("Using cached patient:", patientID);
          return of(parsedCachedData); // Return cached patient as observable
        }
        if (issurl.includes("DSTU2")) {
          const ehrHeaders: HttpHeaders = new HttpHeaders()
            .set("Content-Type", "application/json")
            .set("Accept", "application/json")
            .set(
              "Authorization",
              `Bearer ${localStorage.getItem("accessToken")}`
            );
          const url = `${this.utilityService.rebuild_DSTU2_STU3_Url(issurl)}/Patient/${patientID}`;
          return this.http
            .get<IIdentifiers>(
              url,
              { headers: ehrHeaders }
            )
            .pipe(
              catchError((e: any) => {
                return this.handleError(e);
              }),
              map((response: IIdentifiers) => {
                const stu3Id = response.identifier.filter((id) => {
                  if (id.type && id.type.text === "FHIR STU3") {
                    return id.value;
                  }
                  return null;
                });
                if (stu3Id?.length > 0) {
                  return stu3Id[0].value;
                } else {
                  const errorMessage = 'Warning. Patient Identifier not Found. Please contact your internal Epic Consultant to review this case.';
                  this.appLogger.showGlobalAlert(errorMessage, {url: url});
                }
                return null;
              }),
              mergeMap(async (stu3Id) => this.getEhrDataSets(stu3Id))
            );
        } else {
          return this.getEhrDataSets(patientID);
        }
      })
    );
  }

  getEhrDataSets(id: any) {
    this.spinner.start();
    return this.patientDetailService.getPatient(id)
    .pipe(
      catchError(error => {
        return this.handleError(error);
      }),
      tap((patientData) => {
        // Caching the patient in localstorage
        if (patientData) {
          // Remove any previous patientCache, and only save current patient and track current patient always.
          Object.keys(localStorage)
            .filter(key=> key.startsWith('patientData_'))
            .forEach((key) => localStorage.removeItem(key));

          localStorage.setItem(`patientData_${id}`, JSON.stringify(patientData));
        }
      }),
      finalize(() => this.spinner.end()));
  }

  handleError(error: HttpErrorResponse) {
    const errorMessage = `Unable to process the Request : \n ${error.message} `;
    this.appLogger.showGlobalAlert(errorMessage, { url: error.url ?? undefined });
    console.log(errorMessage);
    return throwError(error);
  }
}
